import PropTypes from 'prop-types';

import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown, X } from "lucide-react"; // Assurez-vous que le paquet est installé

const useOutsideClick = (ref, callback) => {
    const handleClick = useCallback((e) => {
        if (ref.current && !ref.current.contains(e.target)) {
            callback();
        }
    }, [ref, callback]);

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);
        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);
};

const MultipleSelector = ({
    items,
    selectedItems,
    selectionableItems = null,
    onChange = () => {},
    itemIdKey = "id",
    itemNameKey = "name",
    placeholder = "Select items",
    styles = {
        container: "",
        tag: "",
        input: "",
        dropdown: "",
        item: "",
    },
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useOutsideClick(containerRef, () => setIsOpen(false));

    const handleSelect = (itemId) => {
        if (!selectedItems.includes(itemId)) {
            onChange([...selectedItems, itemId]);
        }
    };

    const handleRemove = (itemId) => {
        onChange(selectedItems.filter(id => id !== itemId));
    };

    return (
        <div className="relative" ref={containerRef}>
            <div
                className={`border border-gray-300 rounded-sm px-2 py-1 flex flex-wrap items-center gap-2 cursor-pointer ${styles.container || ""}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                {selectedItems.map(itemId => {
                    const item = items.find(i => i[itemIdKey] === itemId);
                    return item ? (
                        <div key={itemId} className={`bg-blue-100 text-blue-700 px-2 py-1 rounded-md flex items-center gap-1 ${styles.tag || ""}`}>
                            {item[itemNameKey]}
                            <button onClick={(e) => { e.stopPropagation(); handleRemove(itemId); }}>
                                ×
                            </button>
                        </div>
                    ) : null;
                })}
                <div className="flex flex-row items-center gap-2">
                    <input
                        type="text"
                        className={`border-none outline-none flex-grow ${styles.input || ""} py-1`}
                        readOnly
                        placeholder={placeholder}
                    />
                    {isOpen ? 
                        <X size={20} className="ml-2" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} /> :
                        <ChevronDown size={20} className="ml-2" onClick={(e) => { e.stopPropagation(); setIsOpen(true); }} />
                    }
                </div>
            </div>

            {isOpen && (
                <ul className={`absolute bg-white border border-gray-300 rounded-sm p-2 mt-1 w-full max-h-40 overflow-y-auto z-10 ${styles.dropdown || ""}`}>
                    { items
                        .filter(item => !selectedItems.includes(item[itemIdKey]))
                        .filter(item => !selectionableItems || selectionableItems.includes(item[itemIdKey]))
                        .map(item => (
                        <li
                            key={item[itemIdKey]}
                            className={`hover:bg-gray-100 cursor-pointer p-1 ${styles.item || ""}`}
                            onClick={() => { setIsOpen(false); handleSelect(item[itemIdKey]); }}
                        >
                            {item[itemNameKey]}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

MultipleSelector.propTypes = {
        items: PropTypes.array.isRequired,
        selectedItems: PropTypes.array.isRequired,
        selectionableItems: PropTypes.array,
        onChange: PropTypes.func,
        itemIdKey: PropTypes.string,
        itemNameKey: PropTypes.string,
        placeholder: PropTypes.string,
        styles: PropTypes.object,
};

export default MultipleSelector;